/**
 * GoogleOneTap -- Renders nothing visually.
 * Triggers Google One Tap prompt on mount for unauthenticated users.
 * Shows the floating account picker (like Medium, YouTube, etc.)
 *
 * Flow:
 * - Existing user → instant login via optimistic API call → redirect to dashboard
 * - New user → CONSENT_REQUIRED → navigate to login page with pending auth data
 *
 * Placement: App layout, alongside SmartLoginIndicator.
 * Google handles dismiss cooldown automatically -- won't spam users who close it.
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../src/hooks/useAuth';
import { useSocialLogin } from '../../hooks/useSocialLogin';

/** Pages where One Tap should NOT show (sign-in button already handles it) */
const EXCLUDED_PATHS = ['/login', '/sign-in', '/signup', '/register', '/sign-up'];

export const GoogleOneTap: React.FC = () => {
  const { authState } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { triggerGoogleOneTap, pendingSocialAuth } = useSocialLogin();
  const hasTriggered = useRef(false);

  useEffect(() => {
    // Only show for unauthenticated users, not on auth pages, and only once per mount
    if (authState.isAuthenticated) return;
    if (EXCLUDED_PATHS.some((p) => location.pathname.startsWith(p))) return;
    if (pendingSocialAuth) return;
    if (hasTriggered.current) return;
    if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) return;

    hasTriggered.current = true;

    // Small delay so the page content loads first
    const timer = setTimeout(() => {
      triggerGoogleOneTap();
    }, 1500);

    return () => clearTimeout(timer);
  }, [authState.isAuthenticated, location.pathname, pendingSocialAuth, triggerGoogleOneTap]);

  // If One Tap resulted in CONSENT_REQUIRED (new user), redirect to login with pending data
  useEffect(() => {
    if (!pendingSocialAuth) return;
    navigate('/login', {
      state: { pendingSocialAuth, from: location.pathname },
      replace: true,
    });
  }, [pendingSocialAuth, navigate, location.pathname]);

  // Renders nothing -- One Tap is a Google-managed floating UI
  return null;
};

export default GoogleOneTap;
